import { Injectable } from "@angular/core";
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "./auth.service";
import { of, firstValueFrom } from "rxjs";
import { mergeMap, tap, defaultIfEmpty } from "rxjs/operators";

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(private authService: AuthService) { }

    async canActivate(_next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<boolean> {
        if (this.authService.authenticated) {
            return this.getIsAuthenticated();

        }
        return this.refreshSession();
    }

    private getIsAuthenticated() {
        this.authService.restartIdleTimer();
        const isAuthObs = of(this.authService.authenticated);
        const isParentSsoSessionValid = this.authService.isSsoSessionValid()
            .pipe(
                tap(isSsoValid => {
                    if (!isSsoValid) {
                        this.authService.sessionEndLocal();
                        this.authService.authorize();
                    }
                })
            );
        return firstValueFrom(isAuthObs.pipe(
            mergeMap(isAuth => isAuth ? isParentSsoSessionValid : of(isAuth)),
            defaultIfEmpty(false),
        ));
    }

    private refreshSession(): PromiseLike<boolean> {
        return (async () => {
            try {
                const res = await firstValueFrom(this.authService.refreshAuthToken());

                if (!res?.access_token) {
                    if (window.location.pathname !== "/mfa") {
                        sessionStorage.setItem('navigation.history', window.location.pathname);
                    }
                    this.authService.authorize();
                    return false;
                }

                this.authService.sessionBegin(
                    sessionStorage.loginType,
                    res?.access_token,
                    res?.expires_in,
                    res?.refresh_token,
                    res?.refresh_token_expires_in,
                    sessionStorage.clientId);

                return true;
            } catch (_) {
                if (window.location.pathname !== "/mfa") {
                    sessionStorage.setItem('navigation.history', window.location.pathname);
                }
                this.authService.authorize();
                return false;
            }
        })();
    }
}
